import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faDownload, faSearch, faUserGroup, faCheckCircle, faClock, faExclamationTriangle, faList, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import apiServices from "../../../../../services/apiService";
import MetricCard from "../../../../common/Card/MetricCard";

import { getStatusStyles, formatTime, formatDate, getDayOfWeek } from "../../../../../utils/attendanceHelpers";
import { exportToExcel, exportToPDF } from "../../../../../utils/attendanceExportUtils";

import AttendanceActivityModal from "../../../../common/attendance/AttendanceActivityModal";
import AttendanceDailyTable from "../../../../common/attendance/AttendanceDailyTable";
import AttendanceTimelineTable from "../../../../common/attendance/AttendanceTimelineTable";

const AttendanceList = () => {
    const { user } = useSelector((state) => state.auth);

    const getTodayDateString = () => {
        const today = new Date();
        return today.toISOString().split("T")[0];
    };

    const getFirstDayOfMonthString = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        return `${year}-${month}-01`;
    };

    const [startDate, setStartDate] = useState(getTodayDateString());
    const [endDate, setEndDate] = useState(getTodayDateString());
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [expandedRows, setExpandedRows] = useState({});

    // View mode toggle
    const [viewMode, setViewMode] = useState("daily"); // "daily" or "range"

    // Modal state
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

    // Pagination
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Reset pagination when view mode or search changes
    useEffect(() => {
        setPageNumber(1);
    }, [viewMode, searchQuery]);

    useEffect(() => {
        const fetchAttendanceReport = async () => {
            try {
                setLoading(true);
                const siteId = user?.siteId;
                const unitId = user?.branchorunitID;

                const queryEndDate = viewMode === "daily" ? startDate : endDate;

                const response = await apiServices({
                    method: "GET",
                    url: `/CommAttendanceManagement/report?siteId=${siteId}&unitId=${unitId}&startDate=${startDate}&endDate=${queryEndDate}`
                });

                if (response?.success) {
                    const apiData = Array.isArray(response.data?.Data) ? response.data.Data : (Array.isArray(response.data) ? response.data : []);
                    setReportData(apiData);
                }
            } catch (error) {
                console.error("Error fetching CommAttendanceManagement report:", error);
            } finally {
                setLoading(false);
            }
        };

        if (user && startDate && endDate) {
            fetchAttendanceReport();
        }
    }, [user, startDate, endDate, viewMode]);

    // --- DATA PREP FOR DAILY VIEW ---
    const flatAttendanceRecords = useMemo(() => {
        return reportData.map((apiRecord, index) => {
            const statusNorm = (apiRecord.Status || "").toUpperCase();
            return {
                attendanceId: apiRecord.Id || `api-${index}`,
                employeeName: apiRecord.UserName || "—",
                empId: apiRecord.EmployeeCode || apiRecord.UserId || apiRecord.Id || `ZEN000${index}`,
                date: formatDate(apiRecord.AttendanceDate),
                dayOfWeek: getDayOfWeek(apiRecord.AttendanceDate),
                checkIn: formatTime(apiRecord.FirstInTime),
                checkOut: formatTime(apiRecord.LastOutTime),
                workHours: apiRecord.TotalWorkedHoursFormatted || "—",
                status: statusNorm === "SHORTFALL" ? "SHORTFALL" : statusNorm,
                lateMins: apiRecord.LateByMins || 0,
                earlyLeaveMins: apiRecord.EarlyLeaveByMins || 0,
                ot: apiRecord.ExtraTimeMins ? `${apiRecord.ExtraTimeMins} mins` : "—",
                punchHistory: (apiRecord.Scans || []).map((scan) => ({
                    time: formatTime(scan.ScanTime),
                    type: scan.EventType || "—",
                    device: `${scan.ScanMethodName || "Scan"} (${scan.DeviceCode || "N/A"})`
                }))
            };
        }).sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [reportData]);

    const filteredFlatRecords = useMemo(() => {
        const query = searchQuery.toLowerCase();
        return flatAttendanceRecords.filter(r =>
            (r.employeeName || "").toLowerCase().includes(query) ||
            (r.empId || "").toLowerCase().includes(query)
        );
    }, [flatAttendanceRecords, searchQuery]);

    const flatTotalRecords = filteredFlatRecords.length;
    const flatPaginatedData = filteredFlatRecords.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

    // --- DATA PREP FOR RANGE (GROUPED) VIEW ---
    const groupedData = useMemo(() => {
        const groups = {};

        reportData.forEach((apiRecord, index) => {
            const empName = apiRecord.UserName || "Unknown Employee";
            const empId = apiRecord.EmployeeCode || apiRecord.UserId || apiRecord.Id || `ZEN000${index}`;
            const statusNorm = (apiRecord.Status || "").toUpperCase();

            if (!groups[empName]) {
                groups[empName] = {
                    empId: empId,
                    empName: empName,
                    status: 'Active',
                    records: [],
                };
            }

            groups[empName].records.push({
                date: formatDate(apiRecord.AttendanceDate),
                dayOfWeek: getDayOfWeek(apiRecord.AttendanceDate),
                checkIn: formatTime(apiRecord.FirstInTime),
                checkOut: formatTime(apiRecord.LastOutTime),
                workHours: apiRecord.TotalWorkedHoursFormatted || "—",
                status: statusNorm === "SHORTFALL" ? "SHORTFALL" : statusNorm,
                lateMins: apiRecord.LateByMins || 0,
                earlyLeaveMins: apiRecord.EarlyLeaveByMins || 0,
                ot: apiRecord.ExtraTimeMins ? `${apiRecord.ExtraTimeMins} mins` : "—",
                punchHistory: (apiRecord.Scans || []).map((scan) => ({
                    time: formatTime(scan.ScanTime),
                    type: scan.EventType || "—",
                    device: `${scan.ScanMethodName || "Scan"} (${scan.DeviceCode || "N/A"})`
                }))
            });
        });

        const generateDateTrack = (records) => {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const track = [];
            const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

            const timeDiff = end.getTime() - start.getTime();
            const daysDiff = Math.max(0, Math.floor(timeDiff / (1000 * 3600 * 24)));

            for (let i = 0; i <= daysDiff; i++) {
                if (i > 90) break;

                const d = new Date(start);
                d.setDate(d.getDate() + i);
                const dateString = d.toISOString().split("T")[0];
                const dayIdx = d.getDay();
                const dayLabel = days[dayIdx];

                const rec = records.find(r => r.date === dateString);
                let colorClass = "bg-[#E2E8F0]";

                if (rec) {
                    colorClass = getStatusStyles(rec.status).track;
                } else {
                    if (dayIdx !== 0 && dayIdx !== 6) {
                        if (d <= new Date()) colorClass = "bg-[#DC2626]";
                    }
                }

                track.push({ label: dayLabel, colorClass, date: dateString });
            }
            return track;
        };

        return Object.values(groups).map(g => {
            g.dateTrack = generateDateTrack(g.records);
            g.records.sort((a, b) => new Date(b.date) - new Date(a.date));
            return g;
        });
    }, [reportData, startDate, endDate]);

    const filteredGroupedData = useMemo(() => {
        const query = searchQuery.toLowerCase();
        return groupedData.filter(g =>
            g.empName.toLowerCase().includes(query) ||
            g.empId.toLowerCase().includes(query)
        );
    }, [groupedData, searchQuery]);

    const groupedTotalRecords = filteredGroupedData.length;
    const groupedPaginatedData = filteredGroupedData.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

    // --- KPI Metrics ---
    const totalPunches = reportData.length;
    const presentCount = reportData.filter(r => {
        const s = (r.Status || "").toUpperCase();
        return s === "PRESENT" || s === "LATE" || s === "HALF_DAY" || s === "SHORTFALL";
    }).length;
    const lateCount = reportData.filter(r => (r.Status || "").toUpperCase() === "LATE").length;
    const onLeaveCount = reportData.filter(r => (r.Status || "").toUpperCase() === "ON_LEAVE").length;

    const metricCardsData = [
        {
            title: "TOTAL RECORDS",
            value: String(totalPunches).padStart(2, "0"),
            icon: faUserGroup
        },
        {
            title: "PRESENT (ALL DAYS)",
            value: `${String(presentCount).padStart(2, "0")} (${totalPunches > 0 ? Math.round((presentCount / totalPunches) * 100) : 0}%)`,
            icon: faCheckCircle
        },
        {
            title: "LATE COMERS",
            value: String(lateCount).padStart(2, "0"),
            icon: faClock
        },
    ];

    const [isLiveFetching, setIsLiveFetching] = useState(false);

    const toggleRow = (empId) => {
        setExpandedRows(prev => (prev[empId] ? {} : { [empId]: true }));
    };

    const handleRowClick = async (rec, empName) => {
        setSelectedRecord({ ...rec, employeeName: empName });
        setIsDetailModalOpen(true);

        if (viewMode === "daily") {
            try {
                setIsLiveFetching(true);
                const siteId = user?.siteId;
                const unitId = user?.branchorunitID;

                const response = await apiServices({
                    method: "GET",
                    url: `/CommAttendanceManagement/report?siteId=${siteId}&unitId=${unitId}&startDate=${rec.date}&endDate=${rec.date}`
                });

                if (response?.success) {
                    const apiData = Array.isArray(response.data?.Data) ? response.data.Data : (Array.isArray(response.data) ? response.data : []);

                    const liveRecord = apiData.find(d =>
                        (d.EmployeeCode || d.Id || "").toString() === rec.empId.toString() ||
                        d.UserName === empName
                    );

                    if (liveRecord) {
                        const liveHistory = (liveRecord.Scans || []).map((scan) => ({
                            time: formatTime(scan.ScanTime),
                            type: scan.EventType || "—",
                            device: `${scan.ScanMethodName || "Scan"} (${scan.DeviceCode || "N/A"})`
                        }));

                        setSelectedRecord(prev => ({
                            ...prev,
                            punchHistory: liveHistory
                        }));
                    }
                }
            } catch (error) {
                console.error("Error fetching live attendance:", error);
            } finally {
                setIsLiveFetching(false);
            }
        }
    };

    const activeTotalRecords = viewMode === "daily" ? flatTotalRecords : groupedTotalRecords;

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4 scrollbar-hide w-full relative">
            <Breadcrumbs />

            <PageHeadingSection
                mainHeading="Attendance Management"
                subHeading="Track check-ins, lateness, and hours across your workforce."
            >
                <div className="flex flex-col sm:flex-row bg-gray-200/60 p-1 rounded-lg border border-gray-200/50 w-full lg:w-auto">
                    <button
                        onClick={() => {
                            setViewMode("daily");
                            setStartDate(getTodayDateString());
                            setEndDate(getTodayDateString());
                        }}
                        className={`flex-1 flex justify-center items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-all ${viewMode === 'daily' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <FontAwesomeIcon icon={faList} />
                        Daily Attendance
                    </button>
                    <button
                        onClick={() => {
                            setViewMode("range");
                            setStartDate(getFirstDayOfMonthString());
                            setEndDate(getTodayDateString());
                        }}
                        className={`flex-1 flex justify-center items-center gap-2 px-4 py-1.5 rounded-md text-xs font-bold whitespace-nowrap transition-all ${viewMode === 'range' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        Timeline View
                    </button>
                </div>
            </PageHeadingSection>

            {viewMode === "daily" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 shrink-0">
                    {metricCardsData.map((card, index) => (
                        <MetricCard
                            key={index}
                            title={card.title}
                            value={card.value}
                            icon={card.icon}
                        />
                    ))}
                </div>
            )}

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col shrink-0 mt-2">
                <div className="p-5 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 border-b border-gray-100 shrink-0">
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
                        <div className="relative w-full sm:w-56">
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                            <input
                                type="text"
                                placeholder="Search Employee"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50/50 outline-none focus:border-indigo-500 text-gray-700"
                            />
                        </div>
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                            <div className="relative flex items-center bg-[#F8FAFC] border border-gray-200 rounded-lg px-3 py-1.5 font-medium text-gray-600">
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="bg-transparent border-none outline-none font-medium cursor-pointer text-xs"
                                />
                            </div>
                            {viewMode === "range" && (
                                <>
                                    <span className="text-gray-400 font-bold text-xs">TO</span>
                                    <div className="relative flex items-center bg-[#F8FAFC] border border-gray-200 rounded-lg px-3 py-1.5 font-medium text-gray-600">
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="bg-transparent border-none outline-none font-medium cursor-pointer text-xs"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
                        <div className="w-full sm:w-36 relative">
                            <select className="w-full appearance-none border border-gray-200 rounded-lg pl-4 pr-10 py-2 text-sm font-bold text-gray-700 bg-white outline-none focus:border-indigo-500 cursor-pointer">
                                <option>USER TYPE</option>
                                <option>Employee</option>
                            </select>
                            <FontAwesomeIcon icon={faChevronDown} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none" />
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => exportToExcel(filteredGroupedData, startDate, endDate)}
                                disabled={filteredGroupedData.length === 0}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 border border-green-600 text-green-600 hover:bg-green-50 rounded-lg px-4 py-2 text-sm font-bold transition-colors bg-white shadow-sm disabled:opacity-50"
                            >
                                <FontAwesomeIcon icon={faDownload} />
                                Excel
                            </button>
                            <button
                                onClick={() => exportToPDF(filteredGroupedData, startDate, endDate)}
                                disabled={filteredGroupedData.length === 0}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 border border-red-500 text-red-500 hover:bg-red-50 rounded-lg px-4 py-2 text-sm font-bold transition-colors bg-white shadow-sm disabled:opacity-50"
                            >
                                <FontAwesomeIcon icon={faDownload} />
                                PDF
                            </button>
                        </div>
                    </div>
                </div>

                {viewMode === "range" && (
                    <div className="px-5 py-3 flex flex-wrap items-center gap-4 sm:gap-6 border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-wider shrink-0">
                        <span>LEGEND:</span>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm bg-[#22C55E]"></div>
                            <span className="text-gray-600 capitalize">On time</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm bg-[#EA580C]"></div>
                            <span className="text-gray-600 capitalize">Late</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm bg-[#DC2626]"></div>
                            <span className="text-gray-600 capitalize">Absent</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm bg-[#3B82F6]"></div>
                            <span className="text-gray-600 capitalize">Half Day</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm bg-[#EAB308]"></div>
                            <span className="text-gray-600 capitalize">Shortfall</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <div className="w-3 h-3 rounded-sm bg-[#E2E8F0]"></div>
                            <span className="text-gray-600 capitalize">Weekend</span>
                        </div>
                    </div>
                )}

                <div className="w-full overflow-auto relative max-h-[75vh]">
                    {loading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
                            <div className="flex items-center gap-3 text-gray-500 font-bold text-sm">
                                <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                Loading data...
                            </div>
                        </div>
                    ) : null}

                    {!loading && activeTotalRecords === 0 ? (
                        <div className="flex items-center justify-center h-full text-gray-400 font-bold text-sm p-8">
                            No attendance records found.
                        </div>
                    ) : (
                        <>
                            {viewMode === "daily" && (
                                <AttendanceDailyTable
                                    data={flatPaginatedData}
                                    pageNumber={pageNumber}
                                    pageSize={pageSize}
                                    onRowClick={handleRowClick}
                                />
                            )}

                            {viewMode === "range" && (
                                <AttendanceTimelineTable
                                    data={groupedPaginatedData}
                                    pageNumber={pageNumber}
                                    pageSize={pageSize}
                                    expandedRows={expandedRows}
                                    toggleRow={toggleRow}
                                    onRowClick={handleRowClick}
                                />
                            )}
                        </>
                    )}
                </div>

                {activeTotalRecords > 0 && (
                    <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row items-center justify-between gap-4 shrink-0">
                        <span className="text-xs font-bold text-gray-500">
                            Showing {(pageNumber - 1) * pageSize + 1} to {Math.min(pageNumber * pageSize, activeTotalRecords)} of {activeTotalRecords} entries
                        </span>

                        <div className="flex items-center gap-1.5">
                            <button
                                disabled={pageNumber === 1}
                                onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 font-bold text-xs hover:bg-gray-100 disabled:opacity-50 transition-colors"
                            >
                                {'<'}
                            </button>

                            {Array.from({ length: Math.ceil(activeTotalRecords / pageSize) }, (_, i) => i + 1).map(page => {
                                if (page === 1 || page === Math.ceil(activeTotalRecords / pageSize) || (page >= pageNumber - 2 && page <= pageNumber + 2)) {
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => setPageNumber(page)}
                                            className={`w-8 h-8 flex items-center justify-center rounded-lg font-bold text-xs transition-colors ${pageNumber === page ? 'bg-indigo-600 text-white shadow-sm' : 'border border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                                        >
                                            {page}
                                        </button>
                                    );
                                } else if (page === pageNumber - 3 || page === pageNumber + 3) {
                                    return <span key={page} className="px-1 text-gray-400">...</span>;
                                }
                                return null;
                            })}

                            <button
                                disabled={pageNumber >= Math.ceil(activeTotalRecords / pageSize)}
                                onClick={() => setPageNumber(p => p + 1)}
                                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 font-bold text-xs hover:bg-gray-100 disabled:opacity-50 transition-colors"
                            >
                                {'>'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <AttendanceActivityModal
                isOpen={isDetailModalOpen}
                onClose={() => setIsDetailModalOpen(false)}
                selectedRecord={selectedRecord}
                isLiveFetching={isLiveFetching}
            />
        </div>
    );
};

export default AttendanceList;
